import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { colors } from '@styleseat/brand';
import { Inline } from '@styleseat/ui';
import moment from 'moment';
import Title from './Title';
import {
  BodyText,
  Stack,
  Touchable,
} from '../../../../ui';
import { TimeCalendarDatesIcon, TimeCalendarDeleteIcon } from '../../../../ui/Icons';
import { isNoShow as isNoShowCheck } from '../../../../../modules/ProAppointmentState';
import NoteItem from './NoteItem';
import { toTitleCase } from '../../../../../modules/stringUtils';
import type { IHistoryAppointment } from '../../../../../api/Providers/Clients/types';
import type { NoteClickParams } from '../ActivityHistory';
import analytics from '../../../../../modules/analytics';
import {
  PRO_CALENDAR_VIEW_APPOINTMENT,
  PRO_CLIENT_VIEW,
  PRO_CLIENT_VIEW_APPOINTMENT,
} from '../../../../../route-names';
import {
  useLinkTo,
  useParams,
  useRouteName,
} from '../../../../../hooks';
import { RootState } from '../../../../../store/models';
import store from '../../../../../store';

type AppointmentType = 'future' | 'past' | 'next';
type DerivedAppointmentType = AppointmentType | 'late-cancellation' | 'cancelled' | 'no-show';
type VisualProperties = {
  icon?: React.FunctionComponent<{ color?: string; width?: number; height?: number; }>;
  title: string;
  color: string;
  backgroundColor?: string;
};

export type AppointmentItemProps = {
  onNoteClick: (params: NoteClickParams) => void;
  type: AppointmentType;
  appointment: IHistoryAppointment;
  clientName?: string;
};

const APPOINTMENT_TYPES: Record<string, VisualProperties> = {
  next: {
    title: 'Next Appointment',
    color: colors.slate,
    icon: TimeCalendarDatesIcon,
  },
  future: {
    title: 'Future Appointment',
    color: colors.slate,
    icon: TimeCalendarDatesIcon,
  },
  past: {
    title: 'Past Appointment',
    color: colors.ash,
    backgroundColor: colors.superLight,
    icon: TimeCalendarDatesIcon,
  },
  'no-show': {
    title: 'No-show',
    icon: TimeCalendarDeleteIcon,
    color: colors.red,
    backgroundColor: colors.redLite,
  },
  'late-cancellation': {
    title: 'Late Cancellation',
    icon: TimeCalendarDeleteIcon,
    color: colors.red,
    backgroundColor: colors.redLite,
  },
  cancelled: {
    title: 'Cancelled Appointment',
    icon: TimeCalendarDeleteIcon,
    color: colors.red,
    backgroundColor: colors.redLite,
  },
};

const AppointmentItem: React.FC<AppointmentItemProps> = ({
  onNoteClick,
  type,
  appointment,
  clientName,
}) => {
  const isSuperUser = useSelector<RootState, boolean>(store.select.user.isSuperUser);
  const [routeName] = useRouteName();
  const params = useParams();
  const linkTo = useLinkTo();

  const [
    isNoShow, isCancelled, isCheckedOutNslc,
  ] = useMemo(() => [
    isNoShowCheck(appointment),
    appointment.canceled,
    appointment.is_checked_out_late_cancel || appointment.is_checked_out_noshow,
  ], [appointment]);

  const {
    icon,
    color,
    title,
    backgroundColor,
  } = useMemo(() => {
    let derivedType: DerivedAppointmentType = type;
    if (
      appointment?.is_checked_out_late_cancel
      || (
        isCancelled
        && appointment?.nslc_charge_declined
      )
    ) {
      derivedType = 'late-cancellation';
    } else if (isCancelled) {
      derivedType = 'cancelled';
    } else if (isNoShow) {
      derivedType = 'no-show';
    }
    return APPOINTMENT_TYPES[derivedType];
  }, [
    type,
    appointment?.is_checked_out_late_cancel,
    appointment?.nslc_charge_declined,
    isNoShow,
    isCancelled,
  ]);

  const formattedDate = useMemo(() => (
    moment(appointment.local_start).format('MMM D, YYYY')
  ), [appointment.local_start]);

  const formattedTime = useMemo(() => {
    const start = moment(appointment.local_start).format('h:mm A');
    const end = moment(appointment.local_end).format('h:mm A');
    return `${start} - ${end}`;
  }, [
    appointment.local_start,
    appointment.local_end,
  ]);

  const feeExplanation = useMemo(() => {
    if (!(isCheckedOutNslc || appointment.nslc_charge_declined || isNoShow)) {
      return null;
    }
    if (isCancelled) {
      const declineMessage = appointment.nslc_charge_declined ? ' declined' : '';
      const superUserInfo = isSuperUser ? `, Appt ID: ${appointment.id}` : '';
      return `Late cancel charge${declineMessage}${superUserInfo}`;
    }
    if (isNoShow) {
      let noShowCharge = 'No-show charge';
      if (!isCheckedOutNslc) {
        noShowCharge = `${noShowCharge} ${appointment.nslc_charge_declined ? 'declined' : 'waived'}`;
      }
      if (isSuperUser) {
        noShowCharge = `${noShowCharge}, Appt ID: ${appointment.id}`;
      }
      return noShowCharge;
    }
    return null;
  }, [
    isCancelled,
    isNoShow,
    isCheckedOutNslc,
    appointment.nslc_charge_declined,
    isSuperUser,
    appointment.id,
  ]);

  const handleAppointmentClick = useCallback(() => {
    analytics.track('P Clicked Appointment');

    let goToRoute = PRO_CALENDAR_VIEW_APPOINTMENT;
    if (routeName.includes(PRO_CLIENT_VIEW)) {
      goToRoute = PRO_CLIENT_VIEW_APPOINTMENT;
    }
    linkTo(
      goToRoute,
      {
        ...params,
        viewApptId: appointment.id,
      },
    );
  }, [
    appointment.id,
    routeName,
    linkTo,
    params,
  ]);

  return (
    <Stack
      flexGrow={1}
      flexShrink={1}
      space={8}
      testID="AppointmentItem-container"
    >
      <Touchable onClick={handleAppointmentClick} testID="AppointmentItem-clickHandler">
        <Stack flexGrow={1} flexShrink={1} space={8}>
          <Inline
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Title
              color={color}
              backgroundColor={backgroundColor}
              IconComponent={icon || TimeCalendarDatesIcon}
            >
              {title}
            </Title>
            <BodyText
              appearance="smaller"
              color={colors.ash}
              testID="HistoryItem--date"
            >
              {formattedDate}
            </BodyText>
          </Inline>
          <BodyText color={colors.metal} style={{ fontSize: 16 }}>
            {appointment.service_name}
          </BodyText>
          <Inline
            flexGrow={1}
            flexShrink={1}
            space={4}
            flexWrap="wrap"
          >
            <BodyText
              appearance="small"
              color={colors.ash}
              textDecoration={isNoShow || isCancelled ? 'line-through' : undefined}
            >
              {formattedTime}
            </BodyText>
            {feeExplanation ? (
              <BodyText
                appearance="small"
                color={colors.ash}
              >
                {feeExplanation}
              </BodyText>
            ) : null}
          </Inline>
        </Stack>
      </Touchable>

      {appointment.client_note ? (
        <Inline paddingY={4}>
          <Inline
            paddingX={12}
            paddingY={2}
            borderColor={colors.silver}
            borderLeftWidth={4}
            borderStyle="solid"
            flex={1}
          >
            <NoteItem
              type="client"
              renderTitle={() => `Note from ${toTitleCase(clientName)}`}
              blurb={appointment.client_note}
              date={appointment.local_start}
              onClick={onNoteClick}
            />
          </Inline>
        </Inline>
      ) : null}
      {appointment.note ? (
        <Inline paddingY={4}>
          <Inline
            paddingX={12}
            paddingY={2}
            borderColor={colors.silver}
            borderLeftWidth={4}
            borderStyle="solid"
            flex={1}
          >
            <NoteItem
              type="note"
              date={appointment.local_start}
              blurb={appointment.note}
              onClick={onNoteClick}
            />
          </Inline>
        </Inline>
      ) : null}
    </Stack>
  );
};

export default AppointmentItem;
